import { defineMessages } from 'react-intl';

type MessageDescriptor = {
	id: string;
	defaultMessage: string;
	description: string;
};

export const messages: {
	giveFeedbackLabel: MessageDescriptor;
	feedbackTitle: MessageDescriptor;
	requiredFieldsSummary: MessageDescriptor;
	selectFeedbackLabel: MessageDescriptor;
	selectFeedbackPlaceholder: MessageDescriptor;
	typeQuestion: MessageDescriptor;
	typeComment: MessageDescriptor;
	typeBug: MessageDescriptor;
	typeSuggestion: MessageDescriptor;
	fieldLabelBug: MessageDescriptor;
	fieldLabelComment: MessageDescriptor;
	fieldLabelSuggestion: MessageDescriptor;
	fieldLabelQuestion: MessageDescriptor;
	fieldLabelEmpty: MessageDescriptor;
	attachmentsLabel: MessageDescriptor;
	addAttachmentsButton: MessageDescriptor;
	removeAttachment: MessageDescriptor;
	removeAttachmentAriaLabel: MessageDescriptor;
	uploadAriaLabel: MessageDescriptor;
	anonymousTitle: MessageDescriptor;
	anonymousBody: MessageDescriptor;
	canBeContactedLabel: MessageDescriptor;
	privacyPolicyLinkText: MessageDescriptor;
	enrolInResearchLabel: MessageDescriptor;
	optInOptionsLegend: MessageDescriptor;
	cancelButton: MessageDescriptor;
	submitButton: MessageDescriptor;
	validationTypeRequired: MessageDescriptor;
	validationDescriptionRequired: MessageDescriptor;
} = defineMessages({
	giveFeedbackLabel: {
		id: 'twg-microsite.collectFeedback.giveFeedbackLabel',
		defaultMessage: 'Give feedback',
		description:
			'Accessible label for the floating megaphone IconButton that opens the feedback dialog on the TWG microsite home page.',
	},
	feedbackTitle: {
		id: 'twg-microsite-experience.CollectFeedback.feedbackTitle',
		defaultMessage: 'Share your thoughts',
		description: 'Title shown at the top of the custom feedback form',
	},
	requiredFieldsSummary: {
		id: 'twg-microsite-experience.CollectFeedback.requiredFieldsSummary',
		defaultMessage: 'Required fields are marked with an asterisk',
		description: 'Summary note about required fields',
	},
	selectFeedbackLabel: {
		id: 'twg-microsite-experience.CollectFeedback.selectFeedbackLabel',
		defaultMessage: 'Select feedback',
		description: 'Label for the feedback type selector',
	},
	selectFeedbackPlaceholder: {
		id: 'twg-microsite-experience.CollectFeedback.selectFeedbackPlaceholder',
		defaultMessage: 'Choose one',
		description: 'Placeholder in the feedback type dropdown',
	},
	typeQuestion: {
		id: 'twg-microsite-experience.CollectFeedback.typeQuestion',
		defaultMessage: 'Ask a question',
		description: 'Feedback type option: ask a question',
	},
	typeComment: {
		id: 'twg-microsite-experience.CollectFeedback.typeComment',
		defaultMessage: 'Leave a comment',
		description: 'Feedback type option: leave a comment',
	},
	typeBug: {
		id: 'twg-microsite-experience.CollectFeedback.typeBug',
		defaultMessage: 'Report a bug',
		description: 'Feedback type option: report a bug',
	},
	typeSuggestion: {
		id: 'twg-microsite-experience.CollectFeedback.typeSuggestion',
		defaultMessage: 'Suggest an improvement',
		description: 'Feedback type option: suggest an improvement',
	},
	fieldLabelBug: {
		id: 'twg-microsite-experience.CollectFeedback.fieldLabelBug',
		defaultMessage: 'Describe the bug or issue',
		description: 'TextArea label when feedback type is bug',
	},
	fieldLabelComment: {
		id: 'twg-microsite-experience.CollectFeedback.fieldLabelComment',
		defaultMessage: "Let us know what's on your mind",
		description: 'TextArea label when feedback type is comment',
	},
	fieldLabelSuggestion: {
		id: 'twg-microsite-experience.CollectFeedback.fieldLabelSuggestion',
		defaultMessage: "Let us know what you'd like to improve",
		description: 'TextArea label when feedback type is suggestion',
	},
	fieldLabelQuestion: {
		id: 'twg-microsite-experience.CollectFeedback.fieldLabelQuestion',
		defaultMessage: 'What would you like to know?',
		description: 'TextArea label when feedback type is question',
	},
	fieldLabelEmpty: {
		id: 'twg-microsite-experience.CollectFeedback.fieldLabelEmpty',
		defaultMessage: 'Select an option',
		description: 'TextArea label when no feedback type is selected',
	},
	attachmentsLabel: {
		id: 'twg-microsite-experience.CollectFeedback.attachmentsLabel',
		defaultMessage: 'Attachments (optional)',
		description: 'Label for the file attachment section',
	},
	addAttachmentsButton: {
		id: 'twg-microsite-experience.CollectFeedback.addAttachmentsButton',
		defaultMessage: 'Add images or attachments',
		description: 'Button label to open file picker for feedback attachments',
	},
	removeAttachment: {
		id: 'twg-microsite-experience.CollectFeedback.removeAttachment',
		defaultMessage: 'Remove',
		description: 'Button label to remove a selected attachment',
	},
	removeAttachmentAriaLabel: {
		id: 'twg-microsite-experience.CollectFeedback.removeAttachmentAriaLabel',
		defaultMessage: 'Remove {fileName}',
		description:
			'Accessible label for the remove attachment button. {fileName} resolves to the name of the attached file.',
	},
	uploadAriaLabel: {
		id: 'twg-microsite-experience.CollectFeedback.uploadAriaLabel',
		defaultMessage: 'Upload images or attachments',
		description: 'Accessible label for the hidden file input',
	},
	anonymousTitle: {
		id: 'twg-microsite-experience.CollectFeedback.anonymousTitle',
		defaultMessage: 'Anonymous feedback',
		description: 'Section message title when feedback will be submitted anonymously',
	},
	anonymousBody: {
		id: 'twg-microsite-experience.CollectFeedback.anonymousBody',
		defaultMessage:
			'This feedback is being submitted anonymously. Atlassian will not be able to contact you directly regarding this feedback',
		description:
			'Section message body for anonymous feedback. "Atlassian" is the company and brand name.',
	},
	canBeContactedLabel: {
		id: 'twg-microsite-experience.CollectFeedback.canBeContactedLabel',
		defaultMessage:
			'Yes, Atlassian teams can contact me to learn about my experiences to improve Atlassian products and services. I acknowledge the',
		description:
			'Checkbox label to give consent to be contacted (link appended separately). "Atlassian" is the company and brand name.',
	},
	privacyPolicyLinkText: {
		id: 'twg-microsite-experience.CollectFeedback.privacyPolicyLinkText',
		defaultMessage: 'Atlassian Privacy Policy',
		description:
			'Link text for the Atlassian Privacy Policy. "Atlassian" is the company and brand name, and "Atlassian Privacy Policy" is the policy name.',
	},
	enrolInResearchLabel: {
		id: 'twg-microsite-experience.CollectFeedback.enrolInResearchLabel',
		defaultMessage: "I'd like to participate in product research",
		description: 'Checkbox label for opting into product research',
	},
	optInOptionsLegend: {
		id: 'twg-microsite-experience.CollectFeedback.optInOptionsLegend',
		defaultMessage: 'Atlassian opt-in options',
		description:
			'Hidden legend for the opt-in checkboxes fieldset. "Atlassian" is the company and brand name.',
	},
	cancelButton: {
		id: 'twg-microsite-experience.CollectFeedback.cancelButton',
		defaultMessage: 'Cancel',
		description: 'Label for the Cancel button in the feedback form',
	},
	submitButton: {
		id: 'twg-microsite-experience.CollectFeedback.submitButton',
		defaultMessage: 'Send feedback',
		description: 'Label for the Submit button in the feedback form',
	},
	validationTypeRequired: {
		id: 'twg-microsite-experience.CollectFeedback.validationTypeRequired',
		defaultMessage: 'Please select a feedback type',
		description: 'Validation error when no feedback type is selected',
	},
	validationDescriptionRequired: {
		id: 'twg-microsite-experience.CollectFeedback.validationDescriptionRequired',
		defaultMessage: 'Please provide a description',
		description: 'Validation error when description is empty',
	},
});
